import { PDP_DISPLAY_PRICE_SIMPLE_TYPE } from '@/components/blocks/HDMPriceDisplay/constants';
import { FormatPrice } from '@/components/blocks/HDMPriceDisplay/FormatPrice';
import { Linkable } from '@/components/blocks/Linkable';
import { useIsStoreOnlyAvailable } from '@/components/blocks/PLPProductCard/hooks/useIsStoreOnlyAvailable';
import { ProductDetailsAddToCart } from '@/components/blocks/ProductDetails/AddToCart';
import { panelStyles } from '@/components/content/Header/styles/myWishList';
import { useLocalization } from '@/data/Localization';
import { ProductType } from '@/data/types/Product';
import { Box, Stack, Typography } from '@mui/material';
import { FC } from 'react';
import { CDN_BASE_URL, OpenSansFontWeights } from 'shared/custom/constants/commonConstants';
import { CardImage } from '@/components/content/Header/parts/HDMImageCard';

type Props = {
	partNumber: string;
	product: ProductType;
	list: {
		name: string;
		id: string;
	};
	isPurchasedProduct: boolean;
};

export const MyWishlistProductCard: FC<Props> = ({
	partNumber,
	product,
	list,
	isPurchasedProduct,
}) => {
	const routes = useLocalization('Routes');
	const productLabels = useLocalization('HDMProductDetail');
	const whishlistPanelLabels = useLocalization('MyWishlistPanel');
	const productImageUrl = `${CDN_BASE_URL}/productos/${partNumber}/${partNumber}.jpg`;

	const isStoreOnlyAvailable = useIsStoreOnlyAvailable({
		product,
	});

	const name = product?.name ?? '';
	const nameFormatted = name.charAt(0) + name.slice(1).toLowerCase();
	const manufacturer = product?.manufacturer ?? '';
	const manufacturerFormatted = manufacturer.charAt(0) + manufacturer.slice(1).toLowerCase();
	const offerPrice = product?.productPrice?.wholeOffer
		? product?.productPrice?.wholeOffer
		: product?.productPrice?.offer ?? 0;
	const listPrice =
		(product?.productPrice?.wholeOffer
			? product?.productPrice?.wholeList
			: product?.productPrice?.list) ?? 0;

	const discountPercentage = listPrice !== 0 ? (listPrice - offerPrice) / listPrice : 0;
	const formattedDiscount = Math.trunc(discountPercentage * 100);

	const unitPriceMessage = product?.productPrice?.wholeUnit
		? `${productLabels.Price.t()}  ${product?.productPrice?.wholeUnit.toLowerCase()}`
		: null;

	return (
		<Box display="flex" gap="10px" p="10px" width="100%" className="recently-saved__product">
			<CardImage
				key={partNumber}
				width={133}
				height={133}
				url={productImageUrl}
				alt={partNumber}
				className="recently-saved__product-image"
			/>
			<Stack width={'100%'}>
				{isStoreOnlyAvailable && (
					<Typography
						variant="bodyCaption"
						component="span"
						color="alert.main"
						mb="10px"
						fontWeight={OpenSansFontWeights.semibold}
					>
						{whishlistPanelLabels.InStoreExclusive.t()}
					</Typography>
				)}
				{isPurchasedProduct && (
					<Typography
						variant="bodyCaption"
						component="span"
						color="success.main"
						mb="10px"
						fontWeight={OpenSansFontWeights.semibold}
						className="recently-saved__product-status"
					>
						{whishlistPanelLabels.Purchased.t()}
					</Typography>
				)}
				<Typography
					variant="bodySubText"
					component="span"
					color="grey.200"
					mb="10px"
					className="recently-saved__product-list"
				>
					{whishlistPanelLabels.AddedInList.t()}
					<Linkable
						href={{
							pathname: routes.WishListDetails.route.t(),
							query: { id: list?.id },
						}}
						color="secondary.main"
					>
						{list?.name}
					</Linkable>
				</Typography>
				<Typography
					variant="bodySubText"
					component="span"
					color="grey.500"
					mb="5px"
					className="recently-saved__product-title"
				>
					<b className="recently-saved__product-brand">{manufacturerFormatted}</b> {nameFormatted}
				</Typography>
				<Box display="flex" gap="10px">
					<Typography
						variant="bodyCaption"
						component="span"
						color="grey.200"
						mb="10px"
						className="recently-saved__product-sku"
					>
						{productLabels.SKU.t()} {partNumber}
					</Typography>
					<Typography variant="bodyCaption" component="span" color="grey.200" mb="10px">
						{unitPriceMessage}
					</Typography>
				</Box>
				<Box display="flex" gap="5px" mb="10px" alignItems="center">
					<Typography
						variant="bodySubText"
						component="span"
						color="grey.500"
						fontWeight={OpenSansFontWeights.semibold}
						className="recently-saved__product-price"
					>
						<FormatPrice price={offerPrice} variant="xs" type={PDP_DISPLAY_PRICE_SIMPLE_TYPE} />
					</Typography>
					{listPrice > 0 ? (
						<>
							{formattedDiscount > 0 ? (
								<Box
									sx={panelStyles.discountBadge}
									className="recently-saved__product-savings"
								>{`-${formattedDiscount}%`}</Box>
							) : null}
							<Typography
								variant="bodySubText"
								component="span"
								color="grey.200"
								sx={panelStyles.savingsText}
								className="recently-saved__product-price-unit"
							>
								<FormatPrice price={listPrice} variant="xs" type={PDP_DISPLAY_PRICE_SIMPLE_TYPE} />
							</Typography>
						</>
					) : null}
				</Box>
				{isStoreOnlyAvailable ? (
					<Linkable
						href={product?.seo.href}
						type="button"
						variant="outlined"
						className="recently-saved__product-button"
					>
						{productLabels.SeeDetails.t()}
					</Linkable>
				) : (
					<ProductDetailsAddToCart
						item={product}
						useCustomVariant
						sx={panelStyles.disabledButton}
						customAddToCartLabel={productLabels.Add2Cart.t()}
						className="recently-saved__product-button"
						isWishListItem
					/>
				)}
			</Stack>
		</Box>
	);
};
